import React, { useState } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    company: "", // honeypot
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "", company: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div
        className="rounded-3xl border border-[#2F5E43]/10 bg-white p-10 text-center shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
        data-testid="contact-success"
      >
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#E4EDE6]">
          <CheckCircle2 className="h-8 w-8 text-[#2F5E43]" />
        </div>
        <h3 className="font-heading text-2xl font-semibold text-[#111E16]">
          Message sent!
        </h3>
        <p className="mx-auto mt-3 max-w-md leading-relaxed text-[#4A5F52]">
          Thank you for reaching out. Our team typically replies within one to two business
          days at the email you provided.
        </p>
        <button
          onClick={() => setSent(false)}
          className="mt-7 inline-flex items-center justify-center rounded-full bg-[#2F5E43] px-7 py-3 font-medium text-white transition-colors hover:bg-[#244A34]"
          data-testid="contact-send-another"
        >
          Send another message
        </button>
      </div>
    );
  }

  const inputCls =
    "w-full rounded-xl border border-[#E8EBE9] bg-white px-4 py-3 text-[#111E16] outline-none transition-shadow focus:ring-2 focus:ring-[#2F5E43]/30";

  return (
    <form
      onSubmit={submit}
      className="rounded-3xl border border-[#2F5E43]/10 bg-white p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
      data-testid="contact-form"
    >
      <h2 className="font-heading text-xl font-semibold text-[#111E16]">
        Send us a message
      </h2>
      <p className="mt-1.5 text-sm text-[#4A5F52]">
        Fill out the form below and we'll get back to you soon.
      </p>

      {/* Honeypot field - hidden from humans */}
      <div className="absolute left-[-9999px]" aria-hidden="true">
        <label>
          Company
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.company}
            onChange={update("company")}
            data-testid="contact-honeypot"
          />
        </label>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-[#111E16]">Name</label>
          <input
            type="text"
            required
            value={form.name}
            onChange={update("name")}
            placeholder="Your name"
            className={inputCls}
            data-testid="contact-name"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-[#111E16]">Email</label>
          <input
            type="email"
            required
            value={form.email}
            onChange={update("email")}
            placeholder="you@example.com"
            className={inputCls}
            data-testid="contact-email"
          />
        </div>
      </div>

      <div className="mt-4">
        <label className="mb-1.5 block text-sm font-medium text-[#111E16]">Subject</label>
        <input
          type="text"
          required
          value={form.subject}
          onChange={update("subject")}
          placeholder="How can we help?"
          className={inputCls}
          data-testid="contact-subject"
        />
      </div>

      <div className="mt-4">
        <label className="mb-1.5 block text-sm font-medium text-[#111E16]">Message</label>
        <textarea
          required
          rows={5}
          value={form.message}
          onChange={update("message")}
          placeholder="Tell us a little more…"
          className={`${inputCls} resize-none`}
          data-testid="contact-message"
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#2F5E43] px-7 py-3.5 font-medium text-white transition-colors hover:bg-[#244A34] disabled:opacity-70 sm:w-auto"
        data-testid="contact-submit"
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Sending…
          </>
        ) : (
          <>
            <Send className="h-4 w-4" /> Send Message
          </>
        )}
      </button>
    </form>
  );
};
